import {makeAutoObservable} from 'mobx'
import {Lang} from 'yougile-lang'
import {Debouncer, logger,ObjUtil} from 'yougile-util'
import {
  createDataObjectId,
  DataObjectId,
  isDataObjectId,
  TaskListType,
  TasksListId
} from 'yougile-model'
import {Storage} from '@client/storage'
import {UserSettings} from '@client/user-settings'
import {Backend} from '@common/backend'
import {Tracking, TrackingEvent} from '@common/tracking'
import {DefaultListsId, TaskListStore} from '@components/my-tasks/tasks-list/tasks-list-store'
import {
  getAllUserTasksIdsByType
} from '@components/my-tasks/tasks-list/utils'
import {createUseStore} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'

interface State {
  tasksListsOrder: TasksListId[]
  privateTasksLists: TasksListId[]
  visibleAddListType?: TaskListType
  newListTitle: string
  scrollableEl: HTMLElement | undefined
}

interface ActiveDragState {
  listId: TasksListId
  rowId: DataObjectId | string
}

export class TasksListsStore {
  state: State
  updateSettingsDebouncer = new Debouncer(50)
  tasksListStores: Map<TasksListId, TaskListStore> = new Map()
  /**
   * Determines that the promo settings block are seen by user (fixed tooltip with information about new feature)
   */
  public promoSettingsIsSeen: boolean;
  /**
   * When user is dragging a task from any list, this object contains the listId and rowId of the task being dragged
   * Each list can use this object to determine its availability for dropping the task
   */
  public activeDragState: ActiveDragState | null;
  /**
   * This flag is used to trigger UI update when the promo settings block is seen by user
   */
  public uiChangeTrigger: boolean;
  public openedChatId: DataObjectId | undefined;
  /**
   * Key for promo settings block status in local storage
   * @private
   */
  private readonly promoSettingsIsSeenKey: string = `promo-settings-block-is-seen`
  private readonly onDisposeActions: (() => void)[] = []

  constructor() {
    this.state = {
      newListTitle: '',
      tasksListsOrder: [],
      privateTasksLists: [],
      scrollableEl: undefined
    }

    this.uiChangeTrigger = false

    this.promoSettingsIsSeen = !!Storage.getItem(
      this.promoSettingsIsSeenKey
    )

    this.activeDragState = null

    /**
     * Get the opened chat id from the chat hub and listen to opened chat changes
     * is required to highlight the tasks that are related to the opened chat
     */
    const openedChat = ChatUtils.getOpenedChatHub();
    this.openedChatId = openedChat?.id;
    const listenerId = ChatUtils.addChatChangeListener(this.onChatChanged.bind(this))
    this.onDisposeActions.push(() => {
      ChatUtils.removeChatChangeListener(listenerId)
    });

    this.syncTasksLists()

    makeAutoObservable(this, {
      tasksListStores: false
    }, {autoBind: true})
  }

  public dispose() {
    this.onDisposeActions.forEach(action => action())
    this.onDisposeActions.length = 0
  }

  /**
   * Can be used to trigger UI update indicator to all components that are subscribed to this store can update their non-react UI
   * E.g. the promo settings block powered by tippy.js should be updated manually when ui has changed and can affect the block's absolute position
   */
  public triggerUiChange(): void {
    this.uiChangeTrigger = !this.uiChangeTrigger
  }

  /**
   * Every time the UI has been changed, this property returns new value
   */
  public get uiChanged(): boolean {
    return this.uiChangeTrigger
  }

  public registerTaskListStore(listId: TasksListId, tasksListStores: TaskListStore): void {
    this.tasksListStores.set(listId, tasksListStores)
  }

  public unregisterTaskListStore(listId: TasksListId): void {
    this.tasksListStores.delete(listId)
  }

  syncTasksLists(): void {
    const tasksLists = UserSettings.getAllTasksLists()

    if (Backend.features.myTasksLists) {
      if (tasksLists === undefined) {
        this.submitNewList(Lang.msg.myTasksImportantTasks, true)
      }

      this.state.privateTasksLists = tasksLists
        ? ObjUtil.keys(tasksLists)?.filter(listId =>
            Boolean(tasksLists[listId]?.type === 'private')
          )
        : []
    }

    this.state.tasksListsOrder = UserSettings.getTasksListsOrder()
  }

  /**
   * This function should be called once when the user sees the promo settings block
   */
  public setPromoSettingsBlockSeen(): void {
    this.promoSettingsIsSeen = true
    Storage.setItem(this.promoSettingsIsSeenKey, 'true')
  }

  // eslint-disable-next-line max-params
  onDropItem(
    listId: TasksListId,
    droppingItemId: DataObjectId | string,
    overId: DataObjectId | string | undefined,
    prevListId?: TasksListId,
    before?: boolean
  ): void {
    if (!isDataObjectId(droppingItemId)) {
      return
    }

    const listStore = this.tasksListStores.get(listId)

    if (!listStore) {
      logger.error(`List store not found for listId: ${listId}`);
      return;
    }

    if (this.state.privateTasksLists.includes(listId)) {
      void Tracking.send(TrackingEvent.myTasksMovePrivateTaskToAnotherPrivateList)
    } else {
      void Tracking.send(TrackingEvent.myTasksMoveTaskBetweenListsViaMyTaskPage)
    }

    if (!DefaultListsId.includes(listId)) {
      UserSettings.addTaskToCustomList(listId, [droppingItemId], true)
    }

    let prevListStore: TaskListStore | undefined;

    if (prevListId) {
      prevListStore = this.tasksListStores.get(prevListId)
      if (!prevListStore) {
        logger.error(`List store not found for prevListId: ${prevListId}`);
        return;
      }
      if (!DefaultListsId.includes(prevListId)) {
        UserSettings.removeTaskFromCustomList(prevListId, droppingItemId)
        prevListStore.syncTasks();
      }

      const prevListOrder = UserSettings.getOrder(prevListId)
      if (prevListOrder.length) {
        prevListStore.setCustomOrder(prevListOrder.filter(
          prevListItemId => prevListItemId !== droppingItemId
        ))
      } else {
        const prevTasksList = getAllUserTasksIdsByType(prevListId)
        prevListStore?.setCustomOrder(prevTasksList);
      }
    }

    const currentListOrder = UserSettings.getOrder(listId)
    const currentTasksList = getAllUserTasksIdsByType(listId)

    if (overId && isDataObjectId(overId)) {
      if (currentListOrder.includes(overId)) {
        const newOrder = [...currentListOrder].filter(
          listItem => listItem !== droppingItemId
        )
        const indexOfOverId = newOrder.indexOf(overId)
        const pasteIndex = before ? indexOfOverId : indexOfOverId + 1
        newOrder.splice(pasteIndex, 0, droppingItemId)

        listStore.setCustomOrder(newOrder);
        listStore.syncTasks()
        prevListStore?.syncTasks();
        return
      } else {
        const currentOverIdIndex = currentTasksList.indexOf(overId)
        const pasteIndex = before ? currentOverIdIndex : currentOverIdIndex + 1

        if (currentOverIdIndex !== -1) {
          const newOrder = [...currentTasksList].filter(
            currentTaskId => currentTaskId !== droppingItemId
          )
          newOrder.splice(pasteIndex, 0, droppingItemId)
          listStore.setCustomOrder(newOrder)
          listStore.syncTasks()
          prevListStore?.syncTasks();
          return
        }
      }
    }

    const newOrder = currentTasksList.filter(
      listItem => listItem !== droppingItemId
    )
    listStore.setCustomOrder([droppingItemId, ...newOrder])
    listStore.syncTasks()
    prevListStore?.syncTasks();
  }

  onDragStart(listId: TasksListId, rowId: DataObjectId | string): void {
    this.activeDragState = {listId, rowId}
  }

  onDragEnd(): void {
    this.activeDragState = null
  }

  setAddListVisible(listType: TaskListType): void {
    this.state.visibleAddListType = listType
  }

  cancelAddNewList(): void {
    this.state.visibleAddListType = undefined
  }

  setNewListTitle(value: string): void {
    this.state.newListTitle = value
  }

  submitNewList(name?: string, withoutTracking?: boolean): void {
    const newName = (name || this.state.newListTitle)?.trim()
    if (newName) {
      const newItemId = createDataObjectId()
      UserSettings.addTasksList({
        title: newName,
        id: newItemId,
        type: this.state.visibleAddListType
      })

      if (!withoutTracking) {
        if (this.state.visibleAddListType === 'private') {
          void Tracking.send(TrackingEvent.myTasksCreatePrivateListViaMyTaskPage)
        } else {
          void Tracking.send(TrackingEvent.myTasksCreateListViaMyTaskPage)
        }
      }
    }
    this.setNewListTitle('')
    this.cancelAddNewList()
  }

  moveList(listId: TasksListId, up: boolean): void {
    const currentListOrder = this.state.tasksListsOrder
    const currentIndex = currentListOrder.findIndex(
      listElementId => listElementId === listId
    )
    const newIndex = up ? currentIndex - 1 : currentIndex + 1

    if (
      currentIndex < 0 ||
      currentIndex >= currentListOrder.length ||
      newIndex < 0 ||
      newIndex >= currentListOrder.length
    ) {
      return
    }

    const newOrder = [...currentListOrder]
    const temp = newOrder[currentIndex]
    newOrder[currentIndex] = newOrder[newIndex]
    newOrder[newIndex] = temp

    this.state.tasksListsOrder = newOrder

    this.updateSettingsDebouncer.runLatest(() => {
      UserSettings.setTasksListOrder(this.state.tasksListsOrder)
    })

    void Tracking.send(TrackingEvent.myTasksMoveListViaMyTaskPage)
  }

  private onChatChanged(): void {
    const nextId = ChatUtils.getOpenedChatHub()?.id
    if (this.openedChatId !== nextId) {
      this.openedChatId = nextId
    }
  }
}

const [TasksListsContext, useTasksListsStore] = createUseStore(TasksListsStore)
export {TasksListsContext, useTasksListsStore}
